import { formatVND } from "@/lib/format";
import type { MyAffiliateLedger as Ledger } from "@/features/affiliates/my-code-api";

type MilestoneRule = Ledger["config"]["orderMilestones"][number];
export type MilestoneProgress = NonNullable<Ledger["milestoneProgress"]>;

export type MilestoneStep = {
  title: string;
  reward: number;
  /** 0–100, theo điều kiện còn xa nhất (đủ đơn mà thiếu doanh số thì thanh chưa đầy). */
  percent: number;
  remaining: string;
};

/**
 * Mốc thưởng AFF tính theo QUÝ: "2026-Q4" → "Quý 4/2026". Khác khuôn thì trả
 * nguyên — không đoán.
 */
export function periodLabel(period: string): string {
  const quarter = /^(\d{4})-Q([1-4])$/.exec(period);
  return quarter ? `Quý ${quarter[2]}/${quarter[1]}` : period;
}

/** Mốc kế tiếp CHƯA đạt của một bảng (ngưỡng thấp nhất còn thiếu). */
function nextRule(rules: MilestoneRule[], reached: (rule: MilestoneRule) => boolean) {
  return [...rules].sort((a, b) => a.threshold - b.threshold).find((rule) => !reached(rule));
}

const ratio = (have: number, need: number) => (need > 0 ? Math.min(1, Math.max(0, have / need)) : 1);

/**
 * Còn bao nhiêu tới mốc kế tiếp của bảng SỐ ĐƠN. Mốc đạt khi đủ số đơn VÀ đủ sàn
 * doanh số (`minRevenue`) — thiếu một trong hai là chưa đạt, nên câu "còn…" phải
 * nói cả hai, kẻo hứa sai với nhân viên. undefined = đã qua mốc cao nhất.
 */
export function nextOrderStep(
  rules: MilestoneRule[],
  progress: MilestoneProgress,
): MilestoneStep | undefined {
  const rule = nextRule(
    rules,
    (r) => progress.orders >= r.threshold && progress.revenue >= (r.minRevenue ?? 0),
  );
  if (!rule) return undefined;
  const floor = rule.minRevenue ?? 0;
  const needOrders = Math.max(0, rule.threshold - progress.orders);
  const needRevenue = Math.max(0, floor - progress.revenue);
  const remaining =
    needOrders > 0 && needRevenue > 0
      ? `Còn ${needOrders} đơn và ${formatVND(needRevenue)} doanh số`
      : needOrders > 0
        ? `Còn ${needOrders} đơn`
        : `Đủ số đơn, còn ${formatVND(needRevenue)} doanh số`;
  return {
    title: `Mốc ${rule.threshold} đơn`,
    reward: rule.reward,
    percent: Math.round(
      Math.min(ratio(progress.orders, rule.threshold), ratio(progress.revenue, floor)) * 100,
    ),
    remaining,
  };
}

/** Còn bao nhiêu tới mốc kế tiếp của bảng DOANH SỐ. undefined = đã qua mốc cao nhất. */
export function nextRevenueStep(
  rules: MilestoneRule[],
  progress: MilestoneProgress,
): MilestoneStep | undefined {
  const rule = nextRule(rules, (r) => progress.revenue >= r.threshold);
  if (!rule) return undefined;
  return {
    title: `Mốc doanh số ${formatVND(rule.threshold)}`,
    reward: rule.reward,
    percent: Math.round(ratio(progress.revenue, rule.threshold) * 100),
    remaining: `Còn ${formatVND(rule.threshold - progress.revenue)} doanh số`,
  };
}
